import type { QueryClient } from '@zg-rentals/react-query5';
import { useMutation, useQuery, useQueryClient } from '@zg-rentals/react-query5';
import {
  useHpApiAuthenticationV3Handler_flow,
  useHpApiAuthenticationV3Handler_otp,
  useHpApiAuthenticationV3Handler_sendOtp,
  useHpApiUserEmailUpdateV3Handler_confirm,
  useHpApiUserEmailUpdateV3Handler_request,
  useHpApiUserPasswordV3Handler_update,
  useHpApiUserProfileV3Handler_update,
} from '@zg-rentals/ts-hotpads-api-client-react-query';
import type { HpApiResponseDto, HpUserCredentialsDto } from '@zg-rentals/ts-hotpads-api-client';
import type { AuthResponse, InitialUserData, UpdateProfileBody, UserFeedbackBody } from '@/src/lib/api/endpoints/user';
import { user } from '@/src/lib/api/endpoints/user';
import { useInitialUser } from '@/src/lib/auth/InitialUserContext';
import { clearGoogleAvatarCookie, getClientGoogleAvatarUrl } from '@/src/lib/auth/google-avatar';
import { clearPis } from '@/src/lib/ads/pis';
import { setHotpadsComscoreWindowParams } from '@/src/lib/comscore/windowParams';
import { updateHotpadsAnalyticsIdentity } from '@/src/analytics';
import { overrideHotpadsIdentity } from '@/src/lib/identity/browserIdentity';
import { setHotpadsPxParam } from '@/src/lib/identity/perimeterXIdentity';
import { listingDetailKeys, searchKeys } from './queryKeys';
import { removeUserScopedQueries } from './userScopedQueries';

export const userKeys = {
  info: ['user', 'info'] as const,
};

function refreshListingHistoryAfterLogin(queryClient: QueryClient, previousUser: InitialUserData | null | undefined, email: string) {
  if (previousUser?.loggedIn && previousUser.data?.email?.toLowerCase() === email.toLowerCase()) return;
  void queryClient.invalidateQueries({ queryKey: searchKeys.all });
  void queryClient.invalidateQueries({ queryKey: listingDetailKeys.all });
}

export const updateUserCacheFromAuthResponse = (queryClient: QueryClient, authResponse: AuthResponse) => {
  const userToken = authResponse.creds?.userToken ?? '';
  const isAuthenticated = authResponse.loggedIn === true && Boolean(authResponse.data);
  if (!isAuthenticated || !authResponse.data) {
    return;
  }

  overrideHotpadsIdentity({ status: 'authenticated', userToken });
  updateHotpadsAnalyticsIdentity(userToken);
  setHotpadsComscoreWindowParams({ userToken, loggedIn: true });
  setHotpadsPxParam(userToken);

  const email = authResponse.data.email || '';
  const previousUser = queryClient.getQueryData<InitialUserData | null>(userKeys.info);
  const previousData = previousUser?.data;
  // A response without creds can't re-derive the cookie binding; keep the
  // cached photo for the same identity instead of wiping it.
  const photoUrl = userToken
    ? getClientGoogleAvatarUrl(userToken)
    : previousData?.email === email
      ? previousData?.photoUrl
      : undefined;
  queryClient.setQueryData<InitialUserData>(userKeys.info, {
    loggedIn: true,
    data: {
      email,
      firstName: authResponse.data.firstName || '',
      lastName: authResponse.data.lastName || '',
      roles: authResponse.data.roles ?? [],
      passwordSet: authResponse.data.passwordSet,
      photoUrl,
    },
  });
  refreshListingHistoryAfterLogin(queryClient, previousUser, email);
};

/**
 * v3 counterpart to updateUserCacheFromAuthResponse. The v3 envelope splits what
 * v2 returned as one blob: the profile lives on `data`, while the userToken the
 * identity/comscore/PerimeterX integrations key off lives on `session`.
 */
export const updateUserCacheFromV3Credentials = (
  queryClient: QueryClient,
  response: HpApiResponseDto<HpUserCredentialsDto>,
) => {
  const credentials = response.data;
  if (response.error || !response.session.loggedIn || !credentials) {
    return;
  }

  const userToken = response.session.userToken ?? '';
  overrideHotpadsIdentity({ status: 'authenticated', userToken });
  updateHotpadsAnalyticsIdentity(userToken);
  setHotpadsComscoreWindowParams({ userToken, loggedIn: true });
  setHotpadsPxParam(userToken);

  const email = credentials.email || '';
  const previousUser = queryClient.getQueryData<InitialUserData | null>(userKeys.info);
  const previousData = previousUser?.data;
  // Mirrors the v2 helper: without a token we cannot re-derive the avatar cookie
  // binding, so keep the cached photo when the identity has not changed.
  const photoUrl = userToken
    ? getClientGoogleAvatarUrl(userToken)
    : previousData?.email === email
      ? previousData?.photoUrl
      : undefined;
  queryClient.setQueryData<InitialUserData>(userKeys.info, {
    loggedIn: true,
    data: {
      email,
      firstName: credentials.firstName || '',
      lastName: credentials.lastName || '',
      roles: credentials.roles ?? [],
      passwordSet: credentials.passwordSet,
      photoUrl,
    },
  });
  refreshListingHistoryAfterLogin(queryClient, previousUser, email);
};

/**
 * Read the current user from the React Query cache.
 * App Router server layout seeds the first value through InitialUserProvider.
 * Explicit null means confirmed signed out; undefined means auth is still
 * unresolved and will be completed by the streamed server result.
 * Cache is also updated directly by login/logout mutations.
 */
export const useCurrentUser = () => {
  const initialUserFromContext = useInitialUser();

  return useQuery<InitialUserData | null>({
    queryKey: userKeys.info,
    queryFn: () => {
      throw new Error('User should be set via setCurrentUser or login mutation');
    },
    enabled: false,
    staleTime: Infinity,
    initialData: () => initialUserFromContext,
  });
};

/**
 * Returns a setter to update the current user in React Query cache.
 */
export const useSetCurrentUser = () => {
  const queryClient = useQueryClient();

  return (userData: InitialUserData | null) => {
    if (userData === null) {
      overrideHotpadsIdentity({ status: 'anonymous', userToken: '' });
      updateHotpadsAnalyticsIdentity(null);
      setHotpadsComscoreWindowParams({ userToken: '', loggedIn: false });
      setHotpadsPxParam('');
    }
    queryClient.setQueryData(userKeys.info, userData);
  };
};

export const useEmailPasswordLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      user.emailPasswordLogin(email, password),
    onSuccess: (authResponse) => updateUserCacheFromAuthResponse(queryClient, authResponse),
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => user.logout(),
    onSuccess: () => {
      overrideHotpadsIdentity({ status: 'anonymous', userToken: '' });
      updateHotpadsAnalyticsIdentity(null);
      setHotpadsComscoreWindowParams({ userToken: '', loggedIn: false });
      clearPis();
      setHotpadsPxParam('');
      clearGoogleAvatarCookie();
      queryClient.setQueryData(userKeys.info, null);
      removeUserScopedQueries(queryClient);
    },
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateProfileBody) => user.updateProfile(body),
    onSuccess: (authResponse) => updateUserCacheFromAuthResponse(queryClient, authResponse),
    meta: {
      context: { api: 'hotpads-api', details: 'update user profile' },
    },
  });
};

/**
 * Update the user's first/last name via the v3 profile endpoint.
 * The v3 request DTO only carries names — email changes go through
 * useRequestEmailUpdate and useConfirmEmailUpdate below.
 * Transport (CSRF header, envelope-error rejection) comes from the client
 * registered by configureHotpadsApiV3Client at bootstrap.
 */
export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();

  return useHpApiUserProfileV3Handler_update({
    onSuccess: (response) => {
      const profile = response.data;
      if (!profile || response.error || !response.session.loggedIn) {
        return;
      }
      queryClient.setQueryData<InitialUserData | null>(userKeys.info, (prev) =>
        prev?.loggedIn && prev.data
          ? {
              ...prev,
              data: {
                ...prev.data,
                firstName: profile.firstName ?? prev.data.firstName,
                lastName: profile.lastName ?? prev.data.lastName,
              },
            }
          : prev,
      );
    },
    meta: {
      context: { api: 'hotpads-api', details: 'update user profile v3' },
    },
  });
};

/**
 * Set or change the password via the v3 password endpoint, authorized by either the
 * current password (profile page) or an emailed reset token (reset page).
 * The response is a bare boolean, so success only implies passwordSet.
 * Authorization failures arrive as a rejected HotpadsApiV3Error carrying a reason —
 * read it with v3ErrorReason rather than inspecting the response here.
 */
export const useUpdateUserPassword = () => {
  const queryClient = useQueryClient();

  return useHpApiUserPasswordV3Handler_update({
    onSuccess: (response) => {
      if (!response.data || response.error || !response.session.loggedIn) {
        return;
      }
      queryClient.setQueryData<InitialUserData | null>(userKeys.info, (prev) =>
        prev?.loggedIn && prev.data ? { ...prev, data: { ...prev.data, passwordSet: true } } : prev,
      );
    },
    meta: {
      context: { api: 'hotpads-api', details: 'update user password v3' },
    },
  });
};

/**
 * First step of the email change: the backend checks the current password, records the
 * pending change and emails a one-time code to the new address. A wrong password rejects
 * here, before any code goes out. Failures reject as HotpadsApiV3Error — branch on v3ErrorReason.
 */
export const useRequestEmailUpdate = () => {
  return useHpApiUserEmailUpdateV3Handler_request({
    meta: {
      context: { api: 'hotpads-api', details: 'request email update v3' },
    },
  });
};

/**
 * Second step of the email change: redeems the code against the pending change. Success
 * rotates the session and returns its credentials, so the current-user cache is primed
 * from the response rather than refetched.
 */
export const useConfirmEmailUpdate = () => {
  const queryClient = useQueryClient();

  return useHpApiUserEmailUpdateV3Handler_confirm({
    onSuccess: (response) => updateUserCacheFromV3Credentials(queryClient, response),
    meta: {
      context: { api: 'hotpads-api', details: 'confirm email update v3' },
    },
  });
};

export const useFreepassLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email, url }: { email: string; url?: string }) => user.freepassLogin(email, url),
    onSuccess: (authResponse) => updateUserCacheFromAuthResponse(queryClient, authResponse),
  });
};

export const useGoogleAuth = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (idToken: string) => user.googleAuth(idToken),
    onSuccess: (authResponse) => updateUserCacheFromAuthResponse(queryClient, authResponse),
  });
};

export const useAppleAuth = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userJwt: string) => user.appleAuth(userJwt),
    onSuccess: (authResponse) => updateUserCacheFromAuthResponse(queryClient, authResponse),
  });
};

export const useFreepassAuth = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email, authenticator }: { email: string; authenticator: string }) =>
      user.freepassAuth(email, authenticator),
    onSuccess: (authResponse) => updateUserCacheFromAuthResponse(queryClient, authResponse),
  });
};

export const useSendResetPasswordEmail = () => {
  return useMutation({
    mutationFn: (email: string) => user.sendResetPasswordEmail(email),
  });
};

export const useSendFeedback = () => {
  return useMutation({
    mutationFn: (body: UserFeedbackBody) => user.sendFeedback(body),
    meta: {
      context: {
        api: 'hotpads-api',
        details: 'send feedback',
      },
    },
  });
};

/**
 * Ask which sign-in step an email goes to: `default` (the account has a password), `otp`
 * (no account, or a passwordless one — redeeming the code creates the account), or `sso`
 * (a Zillow Group email; follow `forwardUrl` to Okta, which returns to `callbackPath`).
 */
export const useAuthFlow = () => {
  return useHpApiAuthenticationV3Handler_flow({
    meta: {
      context: { api: 'hotpads-api', details: 'resolve auth flow' },
    },
  });
};

/**
 * Email a one-time sign-in code.
 * The response body is a constant `true` for every outcome — the backend will not
 * disclose whether the address has an account or whether throttling kicked in — so
 * callers must treat success as "the step may advance", never as "this account exists".
 * Transport failures (the v3EndpointsEnabled kill switch, rate limiting) still reject.
 */
export const useSendOtp = () => {
  return useHpApiAuthenticationV3Handler_sendOtp({
    meta: {
      context: { api: 'hotpads-api', details: 'send otp sign-in code' },
    },
  });
};

/**
 * Redeem a one-time sign-in code. Success sets the session cookies and returns the
 * credentials, so the current-user cache is primed from the response rather than refetched.
 * Failures reject as HotpadsApiV3Error — branch on v3ErrorReason, not on the response.
 */
export const useVerifyOtp = () => {
  const queryClient = useQueryClient();

  return useHpApiAuthenticationV3Handler_otp({
    onSuccess: (response) => updateUserCacheFromV3Credentials(queryClient, response),
    meta: {
      context: { api: 'hotpads-api', details: 'verify otp sign-in code' },
    },
  });
};
